import * as React from 'react'
import {Box, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, Button, Collapse} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'

import {sectionCardSx, secondaryBtnSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, MUTED, LIGHT_BG, WHITE, PRIMARY_BLUE, WARNING, withOpacity} from '../../../styles/foundation/colors'
import {GAP} from '../../../styles/foundation/spacing'

import {pct, int} from './format'
import {MERCHANT, SKUS, REQUESTS, SUBCATS, PACKS} from './data/noberoFreeze'
import {REVIEW_STATE} from './data/reference'
// Note: SKUS/REQUESTS/SUBCATS/PACKS above are the frozen defaults used only
// when this page is rendered stand-alone (e.g. a future test that mounts it
// without a store). freeze-v2/index.js always renders this page wired to
// the shared store instead — see the default parameter values below — so
// that its counters reflect catalogue/request mutations made elsewhere in
// the module rather than always reading the original seed.
import {SOP_STEPS, SOP_CONDITIONS} from './data/sopCopy'

/**
 * Pure — same pattern as disputes-v2/state/analytics.js and
 * disputes-v2/actions.js: the numbers this page's four tiles (and three of
 * its step "stat" lines) show, computed once from the frozen dataset and
 * exported separately so they are testable without mounting anything. See
 * SopPage.test.js.
 */
export function sopCounters(skus, requests) {
  const skuList = skus || []
  const reqList = requests || []

  const ships = skuList.reduce((n, s) => n + (s.ships || 0), 0)
  // Shipment-weighted, kept unrounded — one real merchant's true spread is
  // 0.12%, and rounding it to zero before pct() sees it would read as "no
  // data" instead of the honest "Under 1%".
  const spreadPct = ships
    ? skuList.reduce((n, s) => n + (s.spread || 0) * (s.ships || 0), 0) / ships
    : 0

  // Share of shipments with sorter-scan evidence on file, i.e. the fraction
  // of ships covered by sorterCount, summed across SKUs then converted to a
  // percentage. Kept unrounded before pct() for the same "Under 1%" reason
  // as spreadPct above.
  const coveragePct = ships
    ? (skuList.reduce((n, s) => n + (s.sorterCount || 0), 0) / ships) * 100
    : 0

  const approved = reqList.filter((r) => r.kind === 'approved')
  const review = reqList.filter((r) => r.kind === 'review')
  const rejected = reqList.filter((r) => r.kind === 'rejected')

  return {
    skus: skuList.length,
    dq: skuList.filter((s) => s.dq).length,
    requests: reqList.length,
    approved: approved.length,
    review: review.length,
    rejected: rejected.length,
    prelim: approved.filter((r) => (r.cyclesElapsed || 0) < 2).length,
    waiting: review.filter((r) => r.state === REVIEW_STATE.ADDITIONAL).length,
    spreadPct,
    spreadLabel: pct(spreadPct),
    coveragePct,
    coverageLabel: pct(coveragePct),
  }
}

/** Plural units for an approved-request level breakdown — matches the source's own hand-written forms ('SKUs' vs 'sub-categories' vs 'combinations'), not a naive +'s'. */
const STEP_ICONS = {
  1: Inventory2OutlinedIcon,
  2: LayersOutlinedIcon,
  3: ImageOutlinedIcon,
  4: AcUnitOutlinedIcon,
  5: AssignmentOutlinedIcon,
  6: CheckCircleOutlineIcon,
  7: AnalyticsOutlinedIcon,
}

/**
 * The step's contract is not three peers. `who` and `needed` are what the step
 * consumes; `next` is what it produces — two inputs and one outcome — so the
 * panel below divides twice, at two weights: a hairline rule between the two inputs
 * ("and"), and a doubled full-bleed rule above the outcome ("therefore"). Consequence
 * already runs top-to-bottom on this page, so the outcome sits BELOW the inputs on
 * that same axis rather than beside them as a third column.
 *
 * The panel's outer edge is BORDER, the same weight as the accordion card it sits
 * inside — an inner frame darker than its own container reads as a containment
 * inversion, so the panel cannot outrank the card around it. Containment here is
 * carried by the fill instead: LIGHT_BG against the white accordion Paper, because a
 * filled region reads by its area where a hairline would have to win on contrast
 * alone. That frees the internal marks — the fold between the two inputs and the
 * rule above the outcome — to run one step darker, at MUTED, since those are meant
 * to be seen dividing the panel's own content rather than competing with the card's
 * edge. The outcome deliberately carries no second fill — any tint dark enough to be
 * seen drops its own SUBTLE label below 4.5:1, and the label tier does not bend for a
 * decoration when the doubled rule already says "therefore".
 *
 * The step card is already a frame and the rail is already a line; three bordered
 * blocks inside would have made four. The two internal marks are folds, not edges —
 * the hairline is centred in its own trough and touches neither panel edge, while the
 * doubled rule spans edge to edge, which divides a container instead of boxing something
 * inside it. The first fold turns with the layout: once the columns stack it becomes a
 * rule across the top of the second block, because a 12px margin alone let the two
 * inputs read as one continuous run.
 *
 * The two inputs share the width equally. `who` is systematically the shorter field
 * across all seven steps, so an uneven split did align their bottoms — but it bought
 * that by starving column 1 to 38 characters a line, and a column that thin reads as
 * leftover space rather than as the twin of the one beside it. Equal columns cost some
 * bottom alignment, which the doubled rule squares off in any case.
 */
const PANEL_PAD = {xs: 1.5, md: 2}
/** Exactly cancels PANEL_PAD, so the outcome's rule reaches the panel's edges. */
const PANEL_BLEED = {xs: -1.5, md: -2}
/**
 * Split evenly either side of the fold, so it reads as a fold rather than a column's
 * border. Kept as tight as that reading allows, because the trough is the one part of the
 * panel's width that sets no type and the longest line in either column already clears its
 * measure by under 4px.
 */
const TROUGH = {xs: 0, md: 1.25}

/**
 * Border widths, and there is no foundation token for those — spacing.js is a margin and
 * padding scale. FOLD_STROKE and OUTCOME_STROKE are written out at these two exact widths
 * because the pair only works as a pair: under the shell's `zoom: 0.8` Chrome floors a
 * border to whole device pixels, so 1px, 1.5px and 2px all come out as ONE device pixel.
 * 2.5px is the first width that survives as two, which is what lets the outcome's rule
 * read as heavier than the fold between the inputs instead of identical to it.
 *
 * PANEL_STROKE differs by colour, not width — it is the panel's own outer edge, held at
 * the accordion card's own BORDER weight so the panel never outranks the card it sits
 * inside (see the containment note above).
 */
const PANEL_STROKE = `1px solid ${BORDER}`
const FOLD_STROKE = `1px solid ${MUTED}`
const OUTCOME_STROKE = `2.5px solid ${MUTED}`

/**
 * Three measure caps, because spacing.js has no measure scale and this region is
 * the module's only continuous prose.
 *
 * The panel's own cap is a budget the two input columns spend, not the outcome: 800
 * authored px puts each column on a 54-character measure, which is where the
 * 150-character `who` fields set three full lines instead of four thin ones. Left
 * uncapped the panel fills the card, which on a 1440px screen ran the wider column
 * to 145 characters a line.
 *
 * The outcome spans both columns, so it only answers to the panel's cap at second
 * hand — roughly twice a column's measure, over 100 characters at its longest, well
 * past a comfortable line. It gets its own cap instead: a full-bleed region has no
 * columns either side to justify running that wide just because the panel can.
 *
 * The lead paragraph above the panel is prose with no column to answer to either, so
 * it keeps its own cap in `ch`, tracking the prose tier rather than the panel's geometry.
 */
const SOP_PANEL_MAX = 800
const LEAD_MEASURE = '68ch'
const OUTCOME_MEASURE = '68ch'

/**
 * The summary row indents its title past a 17px step icon and the 8px gap after it,
 * while AccordionDetails starts at the card's own 16px padding — so every line of the
 * detail hung 25px to the left of the step title it belongs to. That went unnoticed
 * under bare prose and does not survive a panel with a drawn edge. No spacing token
 * equals an icon plus its gap, so the three are added up here.
 */
const DETAIL_PL = '41px'

/**
 * A tier BELOW the prose it heads, and heavier. Today's label is `kpiLabelSx`,
 * i.e. the same SIZE.xs as its own body text — a label the size of its body is
 * just another line of text, which is most of why this region reads loose.
 * 12px authored / 9.6px effective, clear of the module's 8.8px floor.
 * `pb` not `mb`: a grid item's margin would notch the vertical rule beside it.
 */
const sopTermSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.bold,
  fontSize: SIZE['2xs'],
  color: SUBTLE,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  lineHeight: 1.3,
  m: 0,
  pb: 0.75,
}

/**
 * Deliberate departure from the SIZE['2xs'] body tier: this is the module's one page of
 * continuous prose, and 2xs reads as 9.6px effective under the shell's zoom — too small to
 * read comfortably here. SIZE.xs (10.5px effective) matches the button-text floor instead.
 * `m: 0` also clears the UA's 40px margin-inline-start on <dd>.
 */
const sopProseSx = {
  fontFamily: FONT,
  fontSize: SIZE.xs,
  fontWeight: WEIGHT.regular,
  color: NAVY,
  lineHeight: 1.6,
  m: 0,
}

/**
 * One term/description pair. Returns a fragment so dt and dd stay DIRECT
 * children of the <dl> — grouping wrappers are valid HTML but several screen
 * readers drop the list semantics through them, and the two tiers can come from
 * explicit grid placement instead.
 */
function SopBlock({label, children, column = 1, outcome = false}) {
  const fold = !outcome && column === 2
  const place = outcome ? {xs: 1, md: '1 / span 2'} : {md: column}
  const gutter = outcome ? null : column === 1 ? {pr: TROUGH} : {pl: TROUGH}
  const foldSx = fold ? {borderLeft: {xs: 'none', md: FOLD_STROKE}} : null
  // Only the term carries the stacked fold, because it is the block's first element and
  // the rule has to land above the label rather than between label and body.
  const stackedFold = fold
    ? {borderTop: {xs: FOLD_STROKE, md: 'none'}, mt: {xs: 1.5, md: 0}, pt: {xs: 1.5, md: 0}}
    : null
  // The outcome's term runs past the panel's padding so its rule reaches both edges.
  const bleed = outcome ? {mx: PANEL_BLEED, px: PANEL_PAD} : null

  return (
    <>
      <Box
        component='dt'
        sx={{
          ...sopTermSx,
          gridColumn: place,
          gridRow: {md: outcome ? 3 : 1},
          ...gutter,
          ...foldSx,
          ...stackedFold,
          ...bleed,
          ...(outcome ? {mt: PANEL_PAD, pt: PANEL_PAD, borderTop: OUTCOME_STROKE} : null),
        }}
      >
        {label}
      </Box>
      <Box
        component='dd'
        sx={{
          ...sopProseSx,
          gridColumn: place,
          gridRow: {md: outcome ? 4 : 2},
          ...gutter,
          ...foldSx,
          ...(outcome ? {maxWidth: OUTCOME_MEASURE} : null),
        }}
      >
        {children}
      </Box>
    </>
  )
}

function SectionLabel({children}) {
  return (
    <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.sm, color: NAVY}}>
      {children}
    </Typography>
  )
}

/**
 * The Overview & SoP section — the only leaf of that sub-module (see
 * freeze-v2/index.js). Read-only: it states the two conditions that govern
 * every freeze, then walks the seven-step procedure from catalogue to billing,
 * each step linking into the section that performs it.
 *
 * TWO HOSTS, ONE COMPONENT. It is the routed page at `/weight-freeze-v2`, and
 * it is also the body of `SopDialog`, the overlay the freeze dashboard's
 * "Weight freeze SOP" button opens. Nothing here knows which — the difference
 * is entirely in `onNavigate`, whose contract is "take the reader to this
 * section", leaving the host to decide what that costs it. The routed page
 * hands it straight to index.js's section switch; the dialog's host closes the
 * dialog first and then does the same. Do not add a `variant`-style prop to
 * tell the two apart: the moment this file renders differently per host, the
 * page and the overlay start drifting, which is what mounting the same
 * component in both exists to prevent.
 */
export default function SopPage({onNavigate = () => {}, skus = SKUS, requests = REQUESTS, subcats = SUBCATS, packs = PACKS}) {
  const [expanded, setExpanded] = React.useState({})
  const [condOpen, setCondOpen] = React.useState({})

  const counters = sopCounters(skus, requests)
  const freeze = (MERCHANT.type && MERCHANT.type.freeze) || 'none'
  const merchantRaises = freeze === 'merchant'
  const raiser = merchantRaises ? MERCHANT.name : 'Prozo Ops'

  const catCount = Object.keys(subcats).length
  const subCount = Object.values(subcats).reduce((n, arr) => n + arr.length, 0)

  const allOpen = SOP_STEPS.length > 0 && SOP_STEPS.every((s) => expanded[s.n])
  const toggleAll = () => setExpanded(allOpen ? {} : SOP_STEPS.reduce((a, s) => { a[s.n] = true; return a }, {}))

  function stepStat(n) {
    switch (n) {
      case 1:
        return counters.dq
          ? int(counters.skus) + ' SKUs, ' + int(counters.dq) + ' needing attention'
          : int(counters.skus) + ' SKUs, none flagged'
      case 2:
        return int(subCount) + ' sub-categories across ' + int(catCount) + ' categories · ' + int(packs.length) + ' packing materials declared'
      case 3:
        return counters.coverageLabel + ' of your shipments have DWS evidence'
      case 4:
        return counters.requests ? int(counters.requests) + ' requests raised to date by ' + raiser : 'No requests raised yet'
      case 5:
        if (counters.review) return int(counters.review) + ' under review · ' + int(counters.rejected) + ' declined to date'
        return counters.rejected ? 'Nothing under review · ' + int(counters.rejected) + ' declined to date' : 'Nothing under review'
      case 6:
        return counters.approved ? int(counters.approved) + ' approved · ' + int(counters.prelim) + ' still preliminary' : 'Nothing approved yet'
      case 7:
        return counters.skus
          ? 'Charged-weight spread averages ' + counters.spreadLabel + ' across the catalogue, weighted by shipments'
          : 'No shipments recorded yet'
      default:
        return ''
    }
  }

  return (
    <Box sx={{display: 'grid', gap: GAP.lg}}>
      {/* No page header. The heading, its subtitle, the "Requests and statuses"
          button and the account-type banner were all removed on request, so the
          page opens straight onto the two governing conditions. The sidebar row
          and the app's own top bar both already name where the reader is, which
          is what made the heading a third statement of it. */}
      {/* Two condition cards — title and tag always visible; the body prose
          and action button reveal on toggle (default collapsed), matching
          the mockup's own open/onToggle behaviour for these cards. */}
      <Box sx={{display: 'grid', gap: GAP.sm}}>
        <SectionLabel>Two conditions govern every weight freeze</SectionLabel>
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: GAP.lg}}>
          {SOP_CONDITIONS.map((c) => {
            const open = !!condOpen[c.tag]
            const toggle = () => setCondOpen((o) => ({...o, [c.tag]: !o[c.tag]}))
            return (
              <Box key={c.tag} sx={sectionCardSx}>
                <Box
                  role='button'
                  tabIndex={0}
                  aria-expanded={open}
                  onClick={toggle}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      toggle()
                    }
                  }}
                  sx={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1,
                    cursor: 'pointer',
                    '&:focus-visible': {outline: `2px solid ${PRIMARY_BLUE}`, outlineOffset: '2px'},
                  }}
                >
                  <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.sm, color: NAVY, display: 'flex', alignItems: 'center', gap: 0.75}}>
                    {c.section === 'packing' ? <LayersOutlinedIcon sx={{fontSize: 17, color: PRIMARY_BLUE}} /> : <VerifiedUserOutlinedIcon sx={{fontSize: 17, color: PRIMARY_BLUE}} />}
                    {c.title}
                  </Typography>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0}}>
                    <Chip label={c.tag} size='small' sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs'], bgcolor: withOpacity(WARNING, 0.16), color: NAVY}} />
                    <ExpandMoreIcon sx={{fontSize: 20, color: SUBTLE, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s'}} />
                  </Box>
                </Box>
                <Collapse in={open}>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, lineHeight: 1.6, mt: 1}}>{c.body}</Typography>
                  <Box sx={{mt: 1.5}}>
                    <Button sx={secondaryBtnSx} endIcon={<ArrowForwardIcon sx={{fontSize: 14}} />} onClick={() => onNavigate(c.section)}>
                      {c.actionLabel}
                    </Button>
                  </Box>
                </Collapse>
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* Seven-step timeline accordion — hand-rolled rail (Box + CSS Grid), not @mui/lab's Timeline */}
      <Box sx={{display: 'grid', gap: GAP.sm}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
          <SectionLabel>The procedure, {SOP_STEPS.length} steps</SectionLabel>
          <Button
            size='small'
            sx={{...secondaryBtnSx, py: 0.4, ml: 'auto'}}
            startIcon={allOpen ? <UnfoldLessIcon sx={{fontSize: 15}} /> : <UnfoldMoreIcon sx={{fontSize: 15}} />}
            onClick={toggleAll}
          >
            {allOpen ? 'Collapse all' : 'Expand all'}
          </Button>
        </Box>

        <Box component='ol' aria-label={`Weight freeze procedure, ${SOP_STEPS.length} steps`} sx={{listStyle: 'none', m: 0, p: 0}}>
          {SOP_STEPS.map((step, i) => {
            const StepIcon = STEP_ICONS[step.n]
            const last = i === SOP_STEPS.length - 1
            const open = !!expanded[step.n]

            return (
              <Box component='li' key={step.n} sx={{display: 'grid', gridTemplateColumns: '38px minmax(0,1fr)', columnGap: GAP.md}}>
                <Box sx={{position: 'relative', display: 'flex', justifyContent: 'center'}}>
                  {!last && (
                    <Box aria-hidden='true' sx={{position: 'absolute', top: 34, bottom: -8, width: '2px', bgcolor: BORDER}} />
                  )}
                  <Box
                    aria-hidden='true'
                    sx={{
                      width: 30, height: 30, flex: '0 0 30px', borderRadius: '50%',
                      display: 'grid', placeItems: 'center', fontVariantNumeric: 'tabular-nums',
                      bgcolor: open ? PRIMARY_BLUE : withOpacity(PRIMARY_BLUE, 0.12),
                      color: open ? WHITE : PRIMARY_BLUE,
                      fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs,
                    }}
                  >
                    {step.n}
                  </Box>
                </Box>

                <Accordion
                  disableGutters
                  expanded={open}
                  onChange={() => setExpanded((o) => ({...o, [step.n]: !o[step.n]}))}
                  sx={{
                    border: `1px solid ${BORDER}`, borderRadius: '12px !important', boxShadow: 'none',
                    mb: GAP.sm, '&:before': {display: 'none'},
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{'& .MuiAccordionSummary-content': {alignItems: 'center', gap: 1, overflow: 'hidden'}}}>
                    <StepIcon sx={{fontSize: 17, color: PRIMARY_BLUE, flexShrink: 0}} />
                    <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.sm, color: NAVY, flexShrink: 0}}>
                      {step.title}
                    </Typography>
                    <Typography sx={{
                      fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, ml: 'auto', mr: 1,
                      display: {xs: 'none', md: 'block'}, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {stepStat(step.n)}
                    </Typography>
                    <Chip label={step.owner} size='small' sx={{fontFamily: FONT, fontSize: SIZE['2xs'], flexShrink: 0}} />
                  </AccordionSummary>
                  <AccordionDetails sx={{pl: DETAIL_PL}}>
                    {/* The lead stays outside the panel and stays grey: it is the step's why,
                        not part of its contract, and unframed prose above a contained one is
                        what makes the panel read as containment. */}
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, lineHeight: 1.6, maxWidth: LEAD_MEASURE, mb: 1.5}}>
                      {step.purpose}
                    </Typography>
                    <Box
                      component='dl'
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {xs: '1fr', md: 'minmax(0,1fr) minmax(0,1fr)'},
                        columnGap: 0, rowGap: 0, m: 0, mb: 1.5, maxWidth: SOP_PANEL_MAX,
                        p: PANEL_PAD, bgcolor: LIGHT_BG, border: PANEL_STROKE, borderRadius: '10px',
                      }}
                    >
                      <SopBlock label='Who does it' column={1}>{step.who}</SopBlock>
                      <SopBlock label='What is needed' column={2}>{step.needed}</SopBlock>
                      <SopBlock label='What happens next' outcome>{step.next}</SopBlock>
                    </Box>
                    {/* Not every step points somewhere. Step 7 is monitoring, which
                        has no page of its own now, and an unguarded button drew an
                        empty control that navigated to `undefined`. */}
                    {step.section && step.link && (
                      <Button sx={secondaryBtnSx} endIcon={<ArrowForwardIcon sx={{fontSize: 14}} />} onClick={() => onNavigate(step.section)}>
                        {step.link}
                      </Button>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* No footer CTA. Its note and its two buttons were removed on request:
          they were a second route to destinations the sidebar already names. The
          step cards keep their individual links, which go to places it does
          not — all but step 7, whose destination no longer exists. */}
    </Box>
  )
}
